import { AlertCircle } from "lucide-react";
import { useCallback, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { BulkCustomerPhoneInput } from "@/gql/graphql";

interface SimplePhoneInputProps {
  phones: BulkCustomerPhoneInput[];
  onChange: (phones: BulkCustomerPhoneInput[]) => void;
  errors?: Record<string, string>;
}

export function SimplePhoneInput({ phones, onChange, errors }: SimplePhoneInputProps) {
  // Always maintain exactly one phone entry
  const [localPhone, setLocalPhone] = useState<BulkCustomerPhoneInput>(
    phones.length > 0
      ? phones[0]
      : {
          phone: "",
          label: "mobile",
          isPrimary: true,
        }
  );

  const updatePhone = useCallback(
    (field: keyof BulkCustomerPhoneInput, value: any) => {
      const updatedPhone = { ...localPhone, [field]: value };
      setLocalPhone(updatedPhone);

      // Only send non-empty phones to parent
      const phonesToSend = updatedPhone.phone.trim() ? [updatedPhone] : [];
      onChange(phonesToSend);
    },
    [localPhone, onChange]
  );

  return (
    <div className="space-y-2">
      <Label htmlFor="phone-0" className={errors?.["phone-0"] ? "text-red-600 font-medium" : ""}>
        Phone Number
      </Label>
      <Input
        id="phone-0"
        type="tel"
        value={localPhone.phone}
        onChange={(e) => updatePhone("phone", e.target.value)}
        placeholder="Enter phone number (optional)"
        className={`transition-all duration-200 ${errors?.["phone-0"] ? "border-red-500 bg-red-50 focus:ring-red-500 focus:border-red-500 animate-pulse" : ""}`}
      />
      {errors?.["phone-0"] && (
        <div className="flex items-center gap-1 text-sm text-red-600 bg-red-50 px-2 py-1 rounded-md border-l-2 border-red-500">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{errors["phone-0"]}</span>
        </div>
      )}
    </div>
  );
}
